import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "./MapaEntrega.css";

// Icono personalizado para la tienda con color #EBC122
const tiendaIconSvg = `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg"><path fill="#EBC122" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.5 12.5 28.5 12.5 28.5S25 21 25 12.5C25 5.6 19.4 0 12.5 0z"/><circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/></svg>`;
const tiendaIconDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(tiendaIconSvg)}`;
const tiendaIcon = new L.Icon({
  iconUrl: tiendaIconDataUrl,
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41],
  className: "tienda-marker"
});

// Icono personalizado para el cliente
const clienteIconSvg = `<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg"><path fill="#007bff" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.5 12.5 28.5 12.5 28.5S25 21 25 12.5C25 5.6 19.4 0 12.5 0z"/><circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/></svg>`;
const clienteIconDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(clienteIconSvg)}`;
const clienteIcon = new L.Icon({
  iconUrl: clienteIconDataUrl,
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41],
  className: "cliente-marker"
});

// Coordenadas de la tienda en Cala Millor
const tienda = { lat: 39.6021, lng: 3.3845 };

// Bounding box aproximado de Mallorca
const mallorcaBounds = {
  north: 40.10,
  south: 39.15,
  west: 2.30,
  east: 3.50,
};

// Componente para manejar clics en el mapa
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
  });
  return null;
}

// Componente para ajustar el zoom del mapa
function MapBounds({ coords }) {
  const map = useMap();

  useEffect(() => {
    if (coords) {
      const bounds = L.latLngBounds([
        [tienda.lat, tienda.lng],
        [coords.lat, coords.lng],
      ]);
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 15,
      });
    }
  }, [coords, map]);

  return null;
}

export default function MapaEntrega({ onAddressChange, onDistanceChange, onDeliveryCostChange, initialAddress = "" }) {
  const [search, setSearch] = useState(initialAddress);
  const [coords, setCoords] = useState(null);
  const [distancia, setDistancia] = useState(null);
  const [direccionCompleta, setDireccionCompleta] = useState(initialAddress);
  const [mensaje, setMensaje] = useState(null);
  const [costoAdicional, setCostoAdicional] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Función haversine para calcular distancia
  const haversine = (c1, c2) => {
    const R = 6371;
    const dLat = ((c2.lat - c1.lat) * Math.PI) / 180;
    const dLng = ((c2.lng - c1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((c1.lat * Math.PI) / 180) *
        Math.cos((c2.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Reverse geocoding: obtener dirección desde coordenadas
  const obtenerDireccionDesdeCoords = async (lat, lng) => {
    setIsLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`;
      const res = await fetch(url, {
        headers: { "User-Agent": "BikeRentalAdmin/1.0" },
      });
      const data = await res.json();
      if (data && data.display_name) {
        return data.display_name;
      }
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch (error) {
      console.error("Error en reverse geocoding:", error);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } finally {
      setIsLoading(false);
    }
  };

  // Procesar coordenadas y calcular distancia
  const procesarCoordenadas = async (lat, lng, direccionPredefinida = null) => {
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lng);

    // Validar Mallorca
    const dentro =
      latNum > mallorcaBounds.south &&
      latNum < mallorcaBounds.north &&
      lonNum > mallorcaBounds.west &&
      lonNum < mallorcaBounds.east;

    if (!dentro) {
      setMensaje("❌ La ubicación no está dentro de Mallorca");
      setCoords(null);
      setDistancia(null);
      setDireccionCompleta("");
      setCostoAdicional(null);
      if (onAddressChange) onAddressChange("");
      if (onDistanceChange) onDistanceChange(null);
      if (onDeliveryCostChange) onDeliveryCostChange(null);
      return;
    }

    const userCoords = { lat: latNum, lng: lonNum };
    setCoords(userCoords);

    let direccion;
    if (direccionPredefinida) {
      direccion = direccionPredefinida;
    } else {
      direccion = await obtenerDireccionDesdeCoords(latNum, lonNum);
    }
    setDireccionCompleta(direccion);
    setSearch(direccion);

    // Calcular distancia
    const distanciaKm = haversine(tienda, userCoords);
    const distanciaRedondeada = parseFloat(distanciaKm.toFixed(2));
    setDistancia(distanciaRedondeada);

    // Calcular coste adicional si es mayor a 15 km
    let costoAdicionalCalculado = 0;
    if (distanciaRedondeada > 15) {
      const kmExtra = distanciaRedondeada - 15;
      const adicional = kmExtra * 2.5;
      costoAdicionalCalculado = parseFloat(adicional.toFixed(2));
    }
    setCostoAdicional(costoAdicionalCalculado);

    setMensaje("✔️ Ubicación válida dentro de Mallorca");

    // Calcular coste total de entrega (40€ base + suplemento)
    const costeTotal = 40 + costoAdicionalCalculado;

    if (onAddressChange && direccion) {
      onAddressChange(direccion);
    }
    if (onDistanceChange) onDistanceChange(distanciaRedondeada);
    if (onDeliveryCostChange) onDeliveryCostChange(costeTotal, distanciaRedondeada);
  };

  // Buscar dirección con Nominatim
  const buscarDireccion = async () => {
    if (!search.trim()) return;
    
    setShowSuggestions(false);
    setMensaje(null);
    setDistancia(null);
    setIsLoading(true);

    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        search
      )}&format=json&addressdetails=1&limit=1`;
      const res = await fetch(url, {
        headers: { "User-Agent": "BikeRentalAdmin/1.0" },
      });
      const data = await res.json();
      if (data.length === 0) {
        setMensaje("❌ Dirección no encontrada");
        setCoords(null);
        setDistancia(null);
        setDireccionCompleta("");
        setCostoAdicional(null);
        if (onAddressChange) onAddressChange("");
        if (onDistanceChange) onDistanceChange(null);
        if (onDeliveryCostChange) onDeliveryCostChange(null);
        return;
      }
      const { lat, lon } = data[0];
      await procesarCoordenadas(lat, lon);
    } catch {
      setMensaje("❌ Error al buscar la dirección");
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar clic en el mapa
  const handleMapClick = async (latlng) => {
    await procesarCoordenadas(latlng.lat, latlng.lng);
  };

  // Buscar sugerencias mientras se escribe
  useEffect(() => {
    if (!search.trim() || search.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const viewbox = "2.3,39.15,3.5,40.1"; // Mallorca bounds
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          search
        )}&format=json&addressdetails=1&limit=5&viewbox=${viewbox}&bounded=1`;
        const res = await fetch(url, {
          headers: { "User-Agent": "BikeRentalAdmin/1.0" },
        });
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch (error) {
        console.error("Error buscando sugerencias:", error);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search]);

  // Inicializar con dirección inicial si existe
  useEffect(() => {
    if (initialAddress && initialAddress.trim() && !coords) {
      buscarDireccion();
    }
  }, [initialAddress]);

  return (
    <div className="mapa-entrega-container">
      <div className="mapa-search-section">
        <h4 className="mapa-title">📍 Seleccionar dirección de entrega</h4>
        
        <div className="mapa-search-wrapper">
          <input
            type="text"
            className="mapa-search-input"
            placeholder="Buscar dirección o hacer clic en el mapa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                buscarDireccion();
              }
            }}
          />
          <button
            type="button"
            className="mapa-search-button"
            onClick={buscarDireccion}
            disabled={isLoading || !search.trim()}
          >
            {isLoading ? "Buscando..." : "Buscar"}
          </button>
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div className="mapa-suggestions">
            {suggestions.map((sug, index) => (
              <div
                key={index}
                className="mapa-suggestion-item"
                onClick={async () => {
                  setSearch(sug.display_name);
                  setShowSuggestions(false);
                  await procesarCoordenadas(sug.lat, sug.lon, sug.display_name);
                }}
              >
                <strong>{sug.display_name}</strong>
                {sug.address && (
                  <span className="mapa-suggestion-details">
                    {sug.address.city || sug.address.town || sug.address.village || ""}
                    {sug.address.postcode && ` - ${sug.address.postcode}`}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {mensaje && (
          <div className={`mapa-mensaje ${mensaje.includes("✔️") ? "success" : "error"}`}>
            {mensaje}
          </div>
        )}

        {distancia !== null && (
          <div className="mapa-info">
            <p>
              <strong>Distancia a la tienda:</strong> {distancia} km
            </p>
            <p>
              <strong>Dirección:</strong> {direccionCompleta}
            </p>
            <div className="mapa-coste">
              <p>
                <strong>Coste base:</strong> 40.00 € (incluye hasta 15 km)
              </p>
              {costoAdicional > 0 && (
                <p>
                  <strong>Suplemento por {parseFloat((distancia - 15).toFixed(2))} km adicionales:</strong>{" "}
                  {costoAdicional.toFixed(2)} €
                </p>
              )}
              <p className="mapa-coste-total">
                <strong>Total entrega:</strong> {(40 + costoAdicional).toFixed(2)} €
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mapa-container">
        <MapContainer
          center={[39.65, 3.00]}
          zoom={10}
          style={{ height: "100%", width: "100%", zIndex: 0 }}
          scrollWheelZoom={true}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            subdomains="abcd"
          />

          <MapClickHandler onMapClick={handleMapClick} />
          <MapBounds coords={coords} />

          <Marker position={[tienda.lat, tienda.lng]} icon={tiendaIcon}>
            <Popup>
              <div className="text-center">
                <strong>📍 Nuestra tienda</strong>
                <br />
                Cala Millor, Mallorca
              </div>
            </Popup>
          </Marker>

          {coords && (
            <Marker position={[coords.lat, coords.lng]} icon={clienteIcon}>
              <Popup>
                <div className="text-center">
                  <strong>📍 Ubicación de entrega</strong>
                  <br />
                  {direccionCompleta || "Ubicación seleccionada"}
                  <br />
                  <small>Distancia: {distancia} km</small>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>
    </div>
  );
}

